import React, { useEffect, useState, useMemo, useCallback } from "react";
import { NotificationCard, Skeleton } from "@/components";
import { useSelector } from "react-redux";
import { selectAllNotifications } from "../../app/Slices";

const NotificationSideBar = () => {
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("received");

  // Using the entity adapter selector
  const notificationsData = useSelector(selectAllNotifications) || [];
  const notificationLoading =
    useSelector((state) => state.notification?.loading) || false;

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  useEffect(() => {
    if (notificationsData.length > 0) {
      // Filter notifications based on active tab
      const filteredNotifications =
        activeTab === "received"
          ? notificationsData.filter((notif) => notif.userId !== null)
          : notificationsData.filter((notif) => notif.userId === null);

      setNotifications(
        filteredNotifications.map((notification) => ({
          id: notification._id, // Use _id from API response
          image: notification.content?.imageUrl || "", // Ensure it doesn't break if imageUrl is missing
          title: notification.content?.title || "No Title",
          date: formatDate(notification.createdAt),
          description: notification.content?.message || "No Description",
        }))
      );
    }
  }, [notificationsData, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const NotificationSkeleton = () => (
    <div className="flex items-center space-x-4 bg-background rounded-xl p-3">
      <Skeleton className="h-12 w-12 rounded-xl bg-background_2/90" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-[150px] bg-background_2/90" />
        <Skeleton className="h-4 w-[100px] bg-background_2/90" />
      </div>
    </div>
  );

  return (
    <div className="w-full overflow-y-auto flex flex-col gap-4">
      {/* Custom Tabs */}
      <div className="bg-background/60 w-full rounded-2xl shadow-xs overflow-hidden border border-secondary/10">
        <div className="flex w-full p-1">
          <button
            className={`flex-1 py-2.5 px-4 font-nunito rounded-xl text-center font-medium outline-none transition-colors ${
              activeTab === "received"
                ? "bg-secondary/90 text-background font-bold"
                : "bg-transparent text-secondary/80 hover:text-secondary/90"
            }`}
            onClick={() => handleTabChange("received")}
          >
            Received
          </button>
          <button
            className={`flex-1 py-2.5 px-4 font-nunito rounded-xl text-center font-medium outline-none transition-colors ${
              activeTab === "sent"
                ? "bg-secondary/90 text-background font-bold"
                : "bg-transparent text-secondary/80 hover:text-secondary/90"
            }`}
            onClick={() => handleTabChange("sent")}
          >
            Sent
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {notificationLoading ? (
          <>
            <NotificationSkeleton />
            <NotificationSkeleton />
            <NotificationSkeleton />
          </>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              image={notification.image}
              title={notification.title}
              date={notification.date}
              description={notification.description}
            />
          ))
        ) : (
          <p className="text-center text-gray-500 mt-4">
            No notifications available
          </p>
        )}
      </div>
    </div>
  );
};

export default NotificationSideBar;
